import React from 'react';
import { AlertTriangle, Inbox, MapPinOff, Paperclip, SearchX } from 'lucide-react';
import { Button } from '../Button';
import {
    TALENTPOOL_LEER_TEXT,
    arbeitsformLabel,
    dateienFehlen,
    datumDe,
    entfernungText,
    hatStandort,
    loeschFaellig,
    ortText,
    umkreisText,
    type TalentEintrag,
} from '../../services/talentpool/talentpoolLogik';
import { BundesweitChip, RolleChip, StatusChip } from './TalentpoolChips';

/**
 * Liste der Bewerbungen, in der Reihenfolge, die `eintraegeFuer` vorgibt
 * (neueste zuerst; mit Suchpunkt nach Entfernung). Mit Suchpunkt steht je
 * Zeile die Luftlinie und ob der Bewerber den Punkt mit seinem Umkreis
 * erreicht. Wer keine Nadel hat, steht mit „ohne Standort“ da.
 */

interface Props {
    eintraege: TalentEintrag[];
    /** Zahl aller geladenen Bewerbungen (vor dem Filter). */
    gesamt: number;
    filterAktiv: boolean;
    mitSuchpunkt: boolean;
    gewaehltId: string | null;
    /** YYYY-MM-DD, Bezug für „Löschfrist erreicht“. */
    heute: string;
    onWaehle: (id: string) => void;
    onFilterZuruecksetzen: () => void;
}

const Erreicht: React.FC<{ eintrag: TalentEintrag }> = ({ eintrag }) => {
    const { km, erreicht } = eintrag;
    const ton = erreicht === true
        ? 'bg-emerald-100 text-emerald-700'
        : erreicht === false
            ? 'bg-slate-100 text-slate-500'
            : 'bg-amber-50 text-amber-800';
    const text = erreicht === true ? 'erreicht' : erreicht === false ? 'zu weit' : 'unklar';
    return (
        <div className="shrink-0 text-right">
            <p className="text-sm font-bold tabular-nums text-slate-800">{km === null ? '—' : entfernungText(km)}</p>
            <span
                className={`inline-block mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${ton}`}
                title={erreicht === null ? 'Kein Umkreis angegeben oder kein Standort' : undefined}
            >
                {text}
            </span>
        </div>
    );
};

export const TalentpoolListe: React.FC<Props> = ({
    eintraege, gesamt, filterAktiv, mitSuchpunkt, gewaehltId, heute, onWaehle, onFilterZuruecksetzen,
}) => {
    if (gesamt === 0) {
        return (
            <div className="flex flex-col items-center text-center gap-2 text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-2xl p-6">
                <Inbox className="w-8 h-8 text-slate-300" aria-hidden />
                <p className="max-w-xs leading-relaxed">{TALENTPOOL_LEER_TEXT}</p>
            </div>
        );
    }
    if (eintraege.length === 0) {
        return (
            <div className="flex flex-col items-center text-center gap-3 text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-2xl p-6">
                <SearchX className="w-8 h-8 text-slate-300" aria-hidden />
                <p>Keine Bewerbung passt zu den Filtern.</p>
                {filterAktiv && <Button variant="secondary" size="sm" onClick={onFilterZuruecksetzen}>Filter zurücksetzen</Button>}
            </div>
        );
    }

    return (
        <ul className="space-y-2" aria-label="Bewerbungen">
            {eintraege.map((e) => {
                const z = e.zeile;
                const gewaehlt = z.id === gewaehltId;
                const ort = ortText(z);
                const fehlen = dateienFehlen(z);
                const faellig = loeschFaellig(z, heute);
                return (
                    <li key={z.id}>
                        <button
                            type="button"
                            onClick={() => onWaehle(z.id)}
                            aria-current={gewaehlt ? 'true' : undefined}
                            data-glass="karte"
                            className={`w-full text-left bg-white border rounded-2xl p-3 shadow-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 ${
                                gewaehlt ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-slate-200 hover:border-slate-300'
                            }`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1 space-y-1">
                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                        <span className="font-bold text-slate-800 break-words">{z.name}</span>
                                        <StatusChip status={z.status} />
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        <RolleChip rolle={z.rolle} />
                                        {z.bundesweit && <BundesweitChip />}
                                        {z.arbeitsform.length > 0 && (
                                            <span className="text-[11px] text-slate-500">{z.arbeitsform.map(arbeitsformLabel).join(' · ')}</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-500 break-words">
                                        {hatStandort(z) ? (
                                            <>{ort || 'Standort auf der Karte'}{!z.bundesweit && <> · {umkreisText(z)}</>}</>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 font-semibold text-amber-700">
                                                <MapPinOff className="w-3 h-3" aria-hidden /> ohne Standort{ort ? ` (${ort})` : ''}
                                            </span>
                                        )}
                                        <span className="text-slate-400"> · Eingang {datumDe(z.eingegangen_am)}</span>
                                    </p>
                                    {(faellig || fehlen > 0) && (
                                        <div className="flex flex-wrap gap-1.5">
                                            {faellig && (
                                                <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                                    <AlertTriangle className="w-3 h-3" aria-hidden /> Löschfrist erreicht
                                                </span>
                                            )}
                                            {fehlen > 0 && (
                                                <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                                                    <Paperclip className="w-3 h-3" aria-hidden /> {fehlen === 1 ? 'Anhang fehlt noch' : `${fehlen} Anhänge fehlen noch`}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {mitSuchpunkt && <Erreicht eintrag={e} />}
                            </div>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
};
